/* eslint-disable camelcase */
import { Box, Paragraph, Card } from '@deckee/deck-hand';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { telemetry } from '../../../../../services/backend';
import getBoundingAreasByLocation from '../../../../../services/geofencing';
import { getMemberAlerts } from '../../../../../services/member/alerts';
import { selectCurrentMemberId } from '../../../../../services/store/modules/backend-service';
import { selectCurrentLocation } from '../../../../../services/store/modules/geo-service';
import {
  circleWrapper,
  getWeatherAlertIcon,
  siren,
} from '../../../../shared/vector-images';
import LogoSpinner from '../../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import ErrorMessage from '../../../../shared/ErrorMessage/ErrorMessage';
import UnavailableInRegion from '../../../../shared/UnavailableInRegion/UnavailableInRegion';
import { NotificationModel } from '../../../../../services/store/modules/notifications';
import NotificationItem from '../../components/NotificationItem';
import { getNotificationIconType } from './NotificationsFeedPage';

const StyledAlertItem = styled(NotificationItem)`
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLogoSpinner = styled(LogoSpinner)`
  margin-top: ${({ theme }) => theme.space[5]}px;
`;
export interface AlertItem {
  id: string;
  title: string;
  created_at: string;
  createdAt: Date; // This is added dynamically so it becomes friends with notifications
  updated_at: string;
  // icon_url: string; // TODO: These are not being used atm - should prob. be removed if not needed
  read: boolean;
  custom_navigation?: string;
  custom_navigation_parameters?: { id: string };
  type: string;
  subtype: string;
  icon_url: string;
  notificationType: string; // This is added dynamically so it becomes friends with notifications
  // valid_to: string; // TODO: These are not being used atm - should prob. be removed if not needed
}

const AlertsPage: FC<{
  prioritizedNotifications?: NotificationModel[];
  handleSelectNotification: (notification: NotificationModel) => unknown;
  handleSelectAlert: (alert: AlertItem) => unknown;
  hideHeadings?: boolean;
  hideGeoFenceMessage?: boolean;
  hiddenAlertIds?: string[];
  onMorePressAlert?: (alert: AlertItem) => void;
  onMorePressNotification?: (notification: NotificationModel) => void;
}> = ({
  prioritizedNotifications = [],
  handleSelectNotification,
  handleSelectAlert,
  hideHeadings,
  hideGeoFenceMessage,
  hiddenAlertIds = [],
  onMorePressAlert,
  onMorePressNotification,
}) => {
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alertFeatureAvailable, setAlertFeatureAvailable] = useState(false);

  const { latitude, longitude } = useSelector(selectCurrentLocation);
  const currentMemberId = useSelector(selectCurrentMemberId);

  useEffect(() => {
    let isMounted = true;

    const getAlerts = async () => {
      setIsLoading(true);
      try {
        const { activeAlerts } = await getMemberAlerts();

        const memberAlertsSorted = activeAlerts?.sort(
          (a: { created_at: number }, b: { created_at: number }) =>
            b.created_at > a.created_at ? 1 : -1,
        );
        telemetry(
          telemetry.DEBUG,
          `[AlertsContainer.getAlerts] received alert details: ${JSON.stringify(
            activeAlerts,
          )}`,
        );
        if (isMounted) {
          setAlerts(
            memberAlertsSorted.filter(
              (alert: { type: string }) => alert.type !== 'weather',
            ),
          );
          setWeatherAlerts(
            memberAlertsSorted.filter(
              (alert: { type: string }) => alert.type === 'weather',
            ),
          );
        }
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[AlertsContainer.getAlerts] Error retrieving ${JSON.stringify(err)}`,
        );
        if (isMounted) {
          setError('There was a problem loading your alerts.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (currentMemberId && alertFeatureAvailable) {
      getAlerts();
    }

    return () => {
      telemetry(telemetry.DEBUG, '[AlertsContainer.useEffect] unmounted');
      isMounted = false;
    };
  }, [setAlerts, setError, currentMemberId, alertFeatureAvailable]);

  useEffect(() => {
    const boundingAreas = getBoundingAreasByLocation({
      longitude,
      latitude,
    });
    setAlertFeatureAvailable(
      boundingAreas.includes('AUSTRALIA') ||
        boundingAreas.includes('UNITEDSTATES'),
    );
  }, [latitude, longitude, setAlertFeatureAvailable]);

  const isAlertHidden = (alertId: string) => hiddenAlertIds.includes(alertId);
  const visibleWeatherAlerts = weatherAlerts.filter(
    (alert: AlertItem) => !isAlertHidden(alert.id),
  );
  const visibleAlerts = alerts.filter(
    (alert: AlertItem) => !isAlertHidden(alert.id),
  );

  return (
    <>
      {hideHeadings &&
        (visibleWeatherAlerts.length >= 1 ||
          visibleAlerts.length >= 1 ||
          prioritizedNotifications.length >= 1) && (
          <Card.Heading size="compact" pt="5px !important">
            Alerts
          </Card.Heading>
        )}

      <Box p="2">
        {isLoading && !error && <StyledLogoSpinner />}

        {!hideHeadings && alertFeatureAvailable && !isLoading && !error && (
          <Paragraph mb="5px">Active weather warnings</Paragraph>
        )}
        {alertFeatureAvailable &&
          !isLoading &&
          !error &&
          visibleWeatherAlerts?.length >= 1 &&
          visibleWeatherAlerts.map((alert: AlertItem) => (
            <StyledAlertItem
              key={alert.id}
              read={alert.read}
              title={alert.title}
              timestamp={alert.updated_at || alert.created_at}
              icon={getWeatherAlertIcon(alert.subtype)}
              iconUrl={alert.icon_url}
              onClick={() => {
                handleSelectAlert(alert);
              }}
              onMorePress={
                onMorePressAlert ? () => onMorePressAlert(alert) : undefined
              }
            />
          ))}

        {!hideHeadings && alertFeatureAvailable && !isLoading && !error && (
          <Paragraph mb="5px">Alerts</Paragraph>
        )}

        {alertFeatureAvailable &&
          !isLoading &&
          !error &&
          visibleAlerts?.length >= 1 &&
          visibleAlerts.map((alert: AlertItem) => (
            <StyledAlertItem
              key={alert.id}
              read={alert.read}
              title={alert.title}
              timestamp={alert.updated_at || alert.created_at}
              icon={getWeatherAlertIcon(alert.subtype)}
              iconType={getNotificationIconType(alert.type, alert.subtype)}
              iconUrl={alert.icon_url}
              onClick={() => {
                handleSelectAlert(alert);
              }}
              onMorePress={
                onMorePressAlert ? () => onMorePressAlert(alert) : undefined
              }
            />
          ))}

        {prioritizedNotifications?.length > 0 &&
          prioritizedNotifications?.map((notification) => (
            <NotificationItem
              key={notification.id}
              title={notification.heading}
              description={notification.description || ''}
              read={notification.read}
              timestamp={notification.createdAt.toISOString()}
              icon={
                notification.data?.requiredNotification
                  ? circleWrapper(siren())
                  : undefined
              }
              iconType={
                notification.data?.requiredNotification
                  ? 'prioritized'
                  : undefined
              }
              onClick={() => {
                handleSelectNotification(notification);
              }}
              onMorePress={
                onMorePressNotification
                  ? () => onMorePressNotification(notification)
                  : undefined
              }
            />
          ))}

        {!isLoading && error && <ErrorMessage description={error} />}

        {!alertFeatureAvailable && !hideGeoFenceMessage && (
          <UnavailableInRegion />
        )}
      </Box>
    </>
  );
};

AlertsPage.defaultProps = {
  prioritizedNotifications: [],
  hideHeadings: false,
  hideGeoFenceMessage: false,
  hiddenAlertIds: [],
  onMorePressAlert: undefined,
  onMorePressNotification: undefined,
};

export default AlertsPage;
